import React, { useEffect, useState } from "react";
import { QueryWorkItemCounts, QueryWorkItemCountsForMultipleGraders } from "../../../queries/QueryWorkitems";
import { GetQueryWorkersByWorkerId, QueryWorkers } from "../../../queries/Workers";
import { Box, Typography, Divider } from "@mui/material";
import theme from "../../../theme/Theme";
import { AudienceConfig, OptionEntry } from "../../../types/cqa";

interface GraderCountsRowProps {
    project: any;
    client: any;
}

interface GraderCountItem {
    worker: CMxAPI.Worker;
    in_progress_workitems: number;
    completed_workitems: number;
    total_workitems: number;
}

export const GraderCountItem = (props : GraderCountItem) => {
    const { worker, in_progress_workitems, total_workitems } = props;
    // Get initials from first and last name
    const getInitials = (firstName: string, lastName: string) => {
        return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
    };

    return (
        <Box sx={{ 
            display: 'flex', 
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',

            boxShadow: "1",                    
            maxWidth: '150px',
            padding: "12px",
            marginRight: "20px",
            backgroundColor: "white"
        }}>
            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.artisticGrey400?.main,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Typography 
                    sx={{ 
                        color: 'white',
                        fontSize: '14px',
                        fontWeight: 500,
                        variant: "overline"
                    }}
                >
                    {getInitials(worker.first_name, worker.last_name)}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                    variant="h2"
                    sx={{ 
                        color: theme.palette.artisticTextInteractive.main,
                        fontSize: '14px'
                    }}
                >
                    {in_progress_workitems}
                </Typography>
                <Divider 
                    orientation="vertical" 
                    sx={{ 
                        height: 16,
                        borderColor: theme.palette.artisticStroke200.main
                    }} 
                />
                <Typography 
                    variant="h2"
                    sx={{ 
                        color: theme.palette.artisticTextHigh.main,
                        fontSize: '14px'
                    }}
                >
                    {total_workitems}
                </Typography>
            </Box>
        </Box>
    );
};

export const GraderCountsRow = (props: GraderCountsRowProps) => {
    const { client, project } = props;

    const [graderCounts, setGraderCounts] = useState<GraderCountItem[]>([]);

    const GraderCountsRow = graderCounts.map((graderCountItem) => {
        return (
                <GraderCountItem key={graderCountItem.worker.worker_id} {...graderCountItem} />
            )
    })

    useEffect(() => {
        const audience: AudienceConfig = project.project_configuration?.audience;
        const individualsConfigured = Array.isArray(audience?.individuals) && audience.individuals.length > 0;
        const assigneeIds = individualsConfigured
            ? audience.individuals.map((individual: OptionEntry) => individual.value)
            : project.assignee_ids;

        Promise.all([
            client.mutate({
                mutation: QueryWorkItemCounts,
                variables: {
                    workItemQuery: {
                        query: QueryWorkItemCountsForMultipleGraders(assigneeIds, project.collection_id)
                    }
                }
            }),
            client.mutate({
                mutation: QueryWorkers,
                variables: {
                    workerQuery: {
                        query: GetQueryWorkersByWorkerId(assigneeIds)
                    }
                }
            })
        ]).then(([WorkItemCountsResponse, WorkersResponse]) => {
            if(WorkItemCountsResponse?.data?.QueryWorkItems?.data?.length < assigneeIds.length){
                return;
            }
            if(WorkersResponse?.data?.QueryWorker?.data?.length < assigneeIds.length){
                return;
            }

            const graderCounts = assigneeIds.map((assignee: string, index: number) => {
                const in_progress_count_for_worker = index < WorkItemCountsResponse?.data?.QueryWorkItems?.data?.length ? WorkItemCountsResponse?.data?.QueryWorkItems?.data[index]?.in_progress_workitems : -1;
                const completed_count_for_worker = index < WorkItemCountsResponse?.data?.QueryWorkItems?.data?.length ? WorkItemCountsResponse?.data?.QueryWorkItems?.data[index]?.completed_workitems : -1;
                
                return {
                    worker: WorkersResponse.data.QueryWorker.find((worker: any) => worker.worker_id === assignee),
                    in_progress_workitems: in_progress_count_for_worker,
                    completed_workitems: completed_count_for_worker,
                    total_workitems: in_progress_count_for_worker + completed_count_for_worker
                }
            })

            setGraderCounts(graderCounts)
        })
        
    }, [project]);

    return <Box sx={{
        display: "flex",
        flexDirection: "row",
        width: "100%",
        maxWidth: '1200px',  // Add reasonable max width for container
        paddingBottom: "16px",
    }}>{GraderCountsRow}</Box>;
}